import { AuthContextType } from '@auth/AuthProvider';
import NetworkController, { HTTPMethod, HTTPRequest, HTTPResponse } from '@data/controller/NetworkController';
import { ChurchAssetUploadTarget } from '@interfaces/church.interfaces';
import { Launchpad, LaunchpadAttributes, LaunchpadMetadata } from '@interfaces/launchpad.interface';

export interface LaunchpadResult {
  result: Launchpad;
}

export default class LaunchpadController {
  private static baseURL: string = process.env.REACT_APP_SANCTUARY_API_URL || "";

  static async fetchLaunchpads(churchID: string, auth: AuthContextType): Promise<HTTPResponse<{ results: LaunchpadMetadata[] }>> {
    return await NetworkController.performRequestAt<{ results: LaunchpadMetadata[] }>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/v1/church/${churchID}/launchpad`),
      auth.consumeRefreshToken
    );
  }

  // Single-Launchpad responses carry more than the Launchpad itself (latest
  // service, playlists, events); we only read the metadata off them.
  static async fetchLaunchpad(churchID: string, launchpadID: string, auth: AuthContextType): Promise<HTTPResponse<LaunchpadResult>> {
    return await NetworkController.performRequestAt<LaunchpadResult>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.GET, `/v1/church/${churchID}/launchpad/${launchpadID}`),
      auth.consumeRefreshToken
    );
  }

  // Replaces the Launchpad's attributes and its tag together. The API treats a
  // missing tag as giving it up, so every update has to say which tag the
  // Launchpad keeps; null releases it.
  static async updateLaunchpad(
    churchID: string,
    launchpadID: string,
    tag: string | null,
    attributes: LaunchpadAttributes,
    auth: AuthContextType
  ): Promise<HTTPResponse<LaunchpadResult>> {
    return await NetworkController.performRequestAt<LaunchpadResult>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.PATCH, `/v1/church/${churchID}/launchpad/${launchpadID}`, {
        tag,
        attributes
      }),
      auth.consumeRefreshToken
    );
  }

  // A tag is optional here; null creates the Launchpad without one.
  static async createLaunchpad(
    churchID: string,
    tag: string | null,
    attributes: LaunchpadAttributes,
    auth: AuthContextType
  ): Promise<HTTPResponse<LaunchpadResult>> {
    return await NetworkController.performRequestAt<LaunchpadResult>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.POST, `/v1/church/${churchID}/launchpad`, {
        tag,
        attributes
      }),
      auth.consumeRefreshToken
    );
  }

  static async createAssetUploadTarget(
    churchID: string,
    contentType: string,
    fileSizeBytes: number,
    auth: AuthContextType
  ): Promise<HTTPResponse<ChurchAssetUploadTarget>> {
    return await NetworkController.performRequestAt<ChurchAssetUploadTarget>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.POST, `/v1/church/${churchID}/assets/upload-target`, {
        assetType: 'launchpad',
        contentType,
        fileSizeBytes
      }),
      auth.consumeRefreshToken
    );
  }

  static async deleteLaunchpad(churchID: string, launchpadID: string, auth: AuthContextType): Promise<HTTPResponse<void>> {
    return await NetworkController.performRequestAt<void>(
      this.baseURL,
      new HTTPRequest(HTTPMethod.DELETE, `/v1/church/${churchID}/launchpad/${launchpadID}`),
      auth.consumeRefreshToken
    );
  }
}
